import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearSession, isAuthenticated } from '../utils/auth';

/**
 * Mitigation for finding #6 (auth token in localStorage is readable by any
 * JS on the page, i.e. an XSS risk). The real fix is moving to httpOnly
 * cookie-based sessions so client-side JS can't read the token at all, but
 * that requires a backend change (see the SECURITY NOTE in utils/auth.ts)
 * and is out of scope for a client-only pass.
 *
 * In the meantime, this shrinks the *blast radius* of that risk: a token
 * stolen via XSS is only useful for as long as the session stays valid.
 * Auto-clearing the session after a period of no user activity means a
 * stolen token has a bounded lifetime instead of being valid indefinitely
 * (previously `admin_token` in localStorage never expired client-side).
 *
 * This does not defend against an attacker who exfiltrates the token
 * immediately on an active session - only against a token sitting around
 * in storage/logs/history being usable indefinitely afterward.
 */

const DEFAULT_IDLE_TIMEOUT_MINUTES = 30;
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousedown',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
];

function getIdleTimeoutMs(): number {
  const configured = Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES);
  const minutes = Number.isFinite(configured) && configured > 0 ? configured : DEFAULT_IDLE_TIMEOUT_MINUTES;
  return minutes * 60 * 1000;
}

export function useIdleLogout(): void {
  const navigate = useNavigate();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isAuthenticated()) return undefined;

    const idleTimeoutMs = getIdleTimeoutMs();

    const handleIdleTimeout = () => {
      clearSession();
      navigate('/login', { replace: true });
    };

    const resetTimer = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(handleIdleTimeout, idleTimeoutMs);
    };

    resetTimer();
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, resetTimer, { passive: true }));

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [navigate]);
}
